import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Award, Leaf, Heart, MapPin, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { FadeUp, FadeIn, StaggerContainer, StaggerItem, ScaleIn } from '../components/ScrollReveal';

const ABOUT_IMG = 'https://images.pexels.com/photos/14381803/pexels-photo-14381803.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const VANILLA_IMG = 'https://customer-assets.emergentagent.com/job_bb23729c-0dea-4683-8280-e0aa97a36711/artifacts/rpuw479q_IMG_2753.jpeg';

const serif = { fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontWeight: 400 };

const About = () => {
  const { lang } = useLanguage();
  const t = translations[lang].about;
  const common = translations[lang].common;

  // Dictionnaire local pour forcer tes textes exacts tout en gardant le bilinguisme
  const customTexts = {
    fr: {
      storyTitle: "Nés à Nice, sélectionnés à travers le monde",
      storyP1: "Basée à Nice, au cœur de la Côte d’Azur, notre entreprise rayonne à travers la France, l’Europe et les marchés internationaux. Nous bâtissons des relations durables avec des producteurs certifiés agriculture biologiques, Ecocert, Fair Trade et USDA Organic.",
      storyP2: "Nous défendons une vanille sincère, stable et bien sourcée, pensée pour les acheteurs qui recherchent autant la qualité du produit que la fiabilité du partenaire.",
      values: [
        { icon: Eye, title: t.v1Title, desc: t.v1Desc },
        { icon: Award, title: t.v2Title, desc: t.v2Desc },
        { icon: Leaf, title: t.v3Title, desc: t.v3Desc },
        // Remplacement de Passion par Origines certifiées
        { icon: Heart, title: "Origines certifiées", desc: "Produits certifiés, avec des certifications pouvant varier selon l’origine." },
      ]
    },
    en: {
      storyTitle: "Born in Nice, selected worldwide",
      storyP1: "Based in Nice, in the heart of the French Riviera, our company shines across France, Europe, and international markets. We build lasting relationships with producers certified in organic farming, Ecocert, Fair Trade, and USDA Organic.",
      storyP2: "We champion sincere, stable, and well-sourced vanilla, designed for buyers who seek both product quality and partner reliability.",
      values: [
        { icon: Eye, title: t.v1Title, desc: t.v1Desc },
        { icon: Award, title: t.v2Title, desc: t.v2Desc },
        { icon: Leaf, title: t.v3Title, desc: t.v3Desc },
        { icon: Heart, title: "Certified origins", desc: "Certified products, with certifications varying by origin." },
      ]
    }
  };

  const current = customTexts[lang] || customTexts.fr;

  const timeline = [
    { key: 't1', color: '#C9A84C' },
    { key: 't2', color: '#8B4513' },
    { key: 't3', color: '#C9A84C' },
    { key: 't4', color: '#8B4513' },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end pb-16 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 z-0">
          <img src={ABOUT_IMG} alt="Vanilla beans" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C2416]/60" />
        </div>
        <div className="relative z-10 container-max pt-28 text-center w-full">
          <FadeUp>
            <p className="section-overline">{t.overline}</p>
            <h1 className="text-5xl md:text-7xl text-white leading-tight mb-4" style={serif}>
              {t.heroTitle}<br />{t.heroSubtitle}
            </h1>
            <div className="w-10 h-px bg-[#C9A84C] mx-auto mt-4" />
          </FadeUp>
        </div>
      </section>

      {/* STORY */}
      <section className="section-padding bg-[#FAF7F2]" data-testid="about-story">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <p className="section-overline">{t.storyOverline}</p>
              {/* MODIFICATION : Nouveau Titre */}
              <h2 className="text-4xl md:text-5xl text-[#2C2416] mb-5" style={serif}>{current.storyTitle}</h2>
              <div className="gold-divider" />
              <div className="space-y-5 mt-4">
                {/* MODIFICATION : Nouveaux Paragraphes */}
                <p className="text-[#7A6E63] leading-relaxed text-base">{current.storyP1}</p>
                <p className="text-[#2C2416] leading-relaxed text-base font-medium">{current.storyP2}</p>
              </div>
              <div className="mt-7 flex items-center gap-2 text-[#8B4513]">
                <MapPin size={14} />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold">{t.locationTitle}</span>
              </div>
            </FadeUp>
            <ScaleIn delay={0.2}>
              <div className="relative">
                <img src={VANILLA_IMG} alt="Vanilla beans" className="rounded-2xl w-full h-[400px] object-cover" />
                <div className="absolute -bottom-4 -right-4 w-28 h-28 rounded-xl bg-[#F0EBE0] border border-[#E5DDD2] flex flex-col items-center justify-center text-center p-3">
                  <span className="text-2xl font-serif font-bold text-[#8B4513]">4</span>
                  <span className="text-[7px] uppercase tracking-wider text-[#7A6E63] leading-tight mt-1">Origines<br />Certifiées</span>
                </div>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="section-padding bg-[#F0EBE0]" data-testid="about-values">
        <div className="container-max">
          <FadeUp>
            <p className="section-overline text-center">{t.valuesOverline}</p>
            <h2 className="text-4xl md:text-5xl text-center text-[#2C2416] mb-12" style={serif}>{t.valuesTitle}</h2>
          </FadeUp>
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-8" staggerDelay={0.1}>
            {/* MODIFICATION : Mapping sur les nouvelles valeurs */}
            {current.values.map(({ icon: Icon, title, desc }, i) => (
              <StaggerItem key={i}>
                <div className="flex flex-col items-center text-center group">
                  <div className="icon-badge mb-4 group-hover:border-[#8B4513]/40 transition-all">
                    <Icon size={18} className="text-[#8B4513]" />
                  </div>
                  <h3 className="text-sm font-semibold text-[#2C2416] mb-2">{title}</h3>
                  <p className="text-[#7A6E63] text-xs leading-relaxed">{desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* TIMELINE (dark) */}
      <section className="section-padding bg-[#2B2016]" data-testid="about-timeline">
        <div className="container-max">
          <FadeUp>
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C9A84C] mb-3 text-center">{t.timelineOverline}</p>
            <h2 className="text-4xl md:text-5xl text-center text-white mb-14" style={serif}>{t.timelineTitle}</h2>
          </FadeUp>

          <div className="relative max-w-2xl mx-auto">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-white/10" />
            <div className="space-y-10">
              {timeline.map(({ key, color }, i) => (
                <FadeUp key={key} delay={i * 0.1}>
                  <div className="flex gap-8 items-start">
                    <div className="w-12 h-12 rounded-full border flex items-center justify-center shrink-0 z-10 bg-[#2B2016]"
                      style={{ borderColor: `${color}60` }}>
                      <span className="text-xs font-semibold" style={{ color }}>{String(i + 1).padStart(2, '0')}</span>
                    </div>
                    <div className="flex-1 pt-2">
                      <p className="text-[10px] uppercase tracking-[0.2em] mb-2 font-medium" style={{ color }}>
                        {t[`${key}Year`]}
                      </p>
                      <p className="text-white/55 text-sm leading-relaxed">{t[`${key}Desc`]}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="section-padding bg-[#FAF7F2] text-center" data-testid="about-location">
        <div className="container-max">
          <FadeUp>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#E5DDD2]" />
              <MapPin size={16} className="text-[#8B4513]" />
              <div className="h-px w-10 bg-[#E5DDD2]" />
            </div>
            <h2 className="text-4xl text-[#2C2416] mb-3" style={serif}>{t.locationTitle}</h2>
            <p className="text-[#7A6E63] max-w-md mx-auto text-sm leading-relaxed mb-8">{t.locationDesc}</p>
            <Link to="/contact" className="btn-primary">{common.contactUs} <ArrowRight size={14} /></Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default About;